/**
 * NoteDocRegistry — global, reference-counted cache of {@link NoteDoc}s.
 *
 * React subscribers (the editor, useFragment readers, etc.) acquire a
 * NoteDoc via {@link useNoteDoc}, which calls `acquire()` on mount and
 * `release()` on unmount. The registry disposes a NoteDoc after its
 * refcount reaches zero and a grace window (default 5 s) elapses
 * without anyone re-acquiring it.
 *
 * The grace window keeps fast back-and-forth navigation snappy:
 * leaving deck A and returning within 5 s reuses the same NoteDoc
 * (no fresh handshake, no IDB reload). Beyond that, the WebSocket
 * gets cleaned up — important when many decks would otherwise leak
 * sockets across the session.
 *
 * NON-REACT ACCESS
 * ----------------
 * `peek(...)` returns a NoteDoc *only if it's already loaded* —
 * intended for callers that observe-but-don't-own the note's
 * lifecycle (e.g. `getActiveUserNotesDoc().deck(id).peekNoteFragment()`).
 * Returns `null` if no editor / consumer has it open.
 */

import * as Y from 'yjs';

import { createYjsPersistence } from '../internal/yjs-persistence';
import type { NoteDoc } from './note-doc';
import { NoteDoc as NoteDocImpl } from './note-doc';

function noteIdbStoreName(opts: {
  room: string;
  deckId: string;
  noteId: string;
}): string {
  return `flashka-note:${opts.room}:${opts.deckId}:${opts.noteId}`;
}

/**
 * Wipe the local persistence store for a (room, deckId, noteId) triple
 * without needing a full NoteDoc lifecycle. Safe to call when nothing
 * is loaded.
 */
async function wipeNoteIdb(opts: {
  room: string;
  deckId: string;
  noteId: string;
}): Promise<void> {
  const dummy = new Y.Doc();
  const idb = createYjsPersistence(noteIdbStoreName(opts), dummy);
  try {
    await idb.clearData();
  } catch {
    // ignore
  }
  try {
    await idb.destroy();
  } catch {
    // ignore
  }
  dummy.destroy();
}

const DEFAULT_GRACE_MS = 5000;

interface Entry {
  doc: NoteDoc;
  refcount: number;
  /** Pending dispose timer when refcount === 0; null otherwise. */
  graceTimer: ReturnType<typeof setTimeout> | null;
}

export interface NoteDocAcquireOptions {
  party: string;
  room: string;
  deckId: string;
  noteId: string;
  host: string;
  params?: Record<string, string> | (() => Promise<Record<string, string>>);
}

/** Which note a registry entry is: the doc's (party, room, deck key, note). */
export interface NoteDocTarget {
  party: string;
  room: string;
  deckId: string;
  noteId: string;
}

export class NoteDocRegistry {
  private entries = new Map<string, Entry>();
  private graceMs: number;
  /**
   * A per-note counter that moves every time that note's doc changes here —
   * an edit, a remote update, a load, an erase. It outlives the doc itself, so
   * a summary read earlier can tell whether the note has changed since, even
   * after the editor that changed it is gone.
   */
  private versions = new Map<string, number>();
  private versionListeners = new Set<(target: NoteDocTarget) => void>();

  constructor(opts?: { graceMs?: number }) {
    this.graceMs = opts?.graceMs ?? DEFAULT_GRACE_MS;
  }

  private static keyOf(opts: {
    party: string;
    room: string;
    deckId: string;
    noteId: string;
  }): string {
    return `${opts.party}|${opts.room}|${opts.deckId}|${opts.noteId}`;
  }

  /** The note's change counter (see `versions`); 0 when it never changed here. */
  versionOf(target: NoteDocTarget): number {
    return this.versions.get(NoteDocRegistry.keyOf(target)) ?? 0;
  }

  /** Called with the note, every time a note's version moves. */
  onVersionChange(listener: (target: NoteDocTarget) => void): () => void {
    this.versionListeners.add(listener);
    return () => {
      this.versionListeners.delete(listener);
    };
  }

  private bump(target: NoteDocTarget): void {
    const key = NoteDocRegistry.keyOf(target);
    this.versions.set(key, (this.versions.get(key) ?? 0) + 1);
    const at: NoteDocTarget = {
      party: target.party,
      room: target.room,
      deckId: target.deckId,
      noteId: target.noteId,
    };
    for (const listener of [...this.versionListeners]) {
      try {
        listener(at);
      } catch {
        // a listener's failure must not break the edit that triggered it
      }
    }
  }

  /**
   * Get an existing NoteDoc without bumping the refcount or creating
   * one. Returns `null` if none is currently loaded.
   *
   * Use this for opportunistic reads that should degrade gracefully
   * when no editor (or other refcount-holder) is mounted. **Do not**
   * use this for code paths that need the doc to keep existing.
   */
  peek(opts: {
    party: string;
    room: string;
    deckId: string;
    noteId: string;
  }): NoteDoc | null {
    return this.entries.get(NoteDocRegistry.keyOf(opts))?.doc ?? null;
  }

  /**
   * Acquire (create or reuse) a NoteDoc, bumping its refcount. The
   * caller MUST eventually call the returned `release` function (or
   * the `release` method directly) to balance.
   */
  acquire(opts: NoteDocAcquireOptions): { doc: NoteDoc; release: () => void } {
    const key = NoteDocRegistry.keyOf(opts);
    const existing = this.entries.get(key);

    if (existing) {
      existing.refcount++;
      if (existing.graceTimer != null) {
        clearTimeout(existing.graceTimer);
        existing.graceTimer = null;
      }
      return { doc: existing.doc, release: () => this.release(key) };
    }

    const doc = new NoteDocImpl({
      host: opts.host,
      party: opts.party,
      room: opts.room,
      deckId: opts.deckId,
      noteId: opts.noteId,
      params: opts.params,
    });
    const entry: Entry = { doc, refcount: 1, graceTimer: null };
    this.entries.set(key, entry);
    const target: NoteDocTarget = {
      party: opts.party,
      room: opts.room,
      deckId: opts.deckId,
      noteId: opts.noteId,
    };
    doc.doc.on('update', () => this.bump(target));
    return { doc, release: () => this.release(key) };
  }

  /**
   * Release one refcount. When the count reaches zero, schedule a
   * grace window before tearing the NoteDoc down. If anyone re-acquires
   * within the window, the timer is cancelled and the doc is reused.
   */
  private release(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    entry.refcount = Math.max(0, entry.refcount - 1);
    if (entry.refcount > 0) return;
    if (entry.graceTimer != null) clearTimeout(entry.graceTimer);
    entry.graceTimer = setTimeout(() => {
      // Last-second check: someone may have re-acquired during the
      // tick that triggered this callback.
      if (entry.refcount > 0) return;
      this.entries.delete(key);
      void entry.doc.dispose();
    }, this.graceMs);
  }

  /**
   * Forget a NoteDoc and erase its IndexedDB store. Used when the
   * note is being deleted. Cancels any active refcounts and grace
   * timer — callers shouldn't be using the doc after they've asked
   * to delete it.
   *
   * If no live NoteDoc is in the registry (no editor mounted), this
   * still wipes the IndexedDB store via {@link wipeNoteIdb} so any
   * cached state on disk doesn't outlive the deletion.
   */
  async eraseAndForget(opts: {
    party: string;
    room: string;
    deckId: string;
    noteId: string;
  }): Promise<void> {
    const key = NoteDocRegistry.keyOf(opts);
    const entry = this.entries.get(key);
    this.bump(opts);
    if (entry) {
      if (entry.graceTimer != null) clearTimeout(entry.graceTimer);
      this.entries.delete(key);
      await entry.doc.destroyAndEraseLocal();
      return;
    }
    await wipeNoteIdb(opts);
  }

  /** Drop every NoteDoc whose key starts with the given prefix. */
  async eraseAndForgetByDeck(opts: {
    party: string;
    room: string;
    deckId: string;
  }): Promise<void> {
    const prefix = `${opts.party}|${opts.room}|${opts.deckId}|`;
    const keys: string[] = [];
    for (const k of this.entries.keys()) if (k.startsWith(prefix)) keys.push(k);
    for (const k of keys) {
      const entry = this.entries.get(k);
      if (!entry) continue;
      this.bump(entry.doc.location);
      if (entry.graceTimer != null) clearTimeout(entry.graceTimer);
      this.entries.delete(k);
      await entry.doc.destroyAndEraseLocal();
    }
  }

  /** Force-disposes every entry. For tests / sign-out cleanup. */
  async destroyAll(): Promise<void> {
    const entries = [...this.entries.values()];
    this.entries.clear();
    for (const entry of entries) {
      if (entry.graceTimer != null) clearTimeout(entry.graceTimer);
      await entry.doc.dispose();
    }
  }
}

// ---------------------------------------------------------------------------
// Module-global singleton
// ---------------------------------------------------------------------------

/**
 * The app's single NoteDocRegistry. Used by every consumer
 * (`useNoteDoc`, `ScopedNotes`, etc.) so refcounts compose across
 * unrelated React subtrees.
 */
export const noteDocRegistry = new NoteDocRegistry();
